import { analytics } from '../utils/analytics';
import { logger } from './logger';
import { notificationService } from './notifications';

interface ErrorDetails {
  code?: string;
  message: string;
  stack?: string;
  metadata?: Record<string, any>;
  timestamp?: Date;
  handled?: boolean;
}

class ErrorService {
  private static instance: ErrorService;
  private errors: ErrorDetails[] = [];
  private readonly MAX_STORED_ERRORS = 100;
  private readonly ERROR_SAMPLING_RATE = 0.1; // Only log 10% of non-critical errors in production

  private constructor() {
    this.initializeErrorHandlers();
  }

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService();
    }
    return ErrorService.instance;
  }

  private initializeErrorHandlers() {
    // Global error handler
    window.onerror = (message, source, lineno, colno, error) => {
      this.handleError(error || new Error(String(message)), {
        source,
        line: lineno,
        column: colno,
        type: 'uncaught_exception',
      });
      return false;
    };

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason, {
        type: 'unhandled_promise_rejection',
      });
    });

    // Network error handler
    if (typeof window !== 'undefined' && 'fetch' in window) {
      const originalFetch = window.fetch;
      window.fetch = async function(...args) {
        try {
          const response = await originalFetch.apply(this, args);
          if (!response.ok) {
            ErrorService.getInstance().handleError(
              new Error(`HTTP error ${response.status}: ${response.statusText}`),
              {
                type: 'fetch_error',
                url: typeof args[0] === 'string' ? args[0] : args[0].url,
                status: response.status,
                statusText: response.statusText,
              }
            );
          }
          return response;
        } catch (error) {
          ErrorService.getInstance().handleError(error, {
            type: 'fetch_network_error',
            url: typeof args[0] === 'string' ? args[0] : args[0].url,
          });
          throw error;
        }
      };
    }
  }

  handleError(error: Error | unknown, metadata?: Record<string, any>): ErrorDetails {
    const errorDetails = this.normalizeError(error);
    
    // Add metadata
    errorDetails.metadata = {
      ...errorDetails.metadata,
      ...metadata,
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    
    errorDetails.timestamp = new Date();
    errorDetails.handled = true;

    // Store error (with limit)
    this.addToErrorStore(errorDetails);

    // Log error
    this.logError(errorDetails);

    // Track in analytics (with sampling in production)
    if (this.shouldTrackError(errorDetails)) {
      analytics.trackError(errorDetails);
    }

    // Show user notification if appropriate
    this.notifyUser(errorDetails);

    return errorDetails;
  }

  private addToErrorStore(error: ErrorDetails) {
    this.errors.unshift(error);
    
    // Limit the number of stored errors
    if (this.errors.length > this.MAX_STORED_ERRORS) {
      this.errors = this.errors.slice(0, this.MAX_STORED_ERRORS);
    }
  }

  private shouldTrackError(error: ErrorDetails): boolean {
    // Always track critical errors
    if (this.isErrorCritical(error)) {
      return true;
    }
    
    // In development, track all errors
    if (process.env.NODE_ENV === 'development') {
      return true;
    }
    
    // In production, sample non-critical errors
    return Math.random() < this.ERROR_SAMPLING_RATE;
  }

  private isErrorCritical(error: ErrorDetails): boolean {
    // Define what makes an error critical
    const criticalErrorTypes = [
      'auth_error',
      'data_loss',
      'security_violation',
      'api_unavailable',
    ];
    
    return criticalErrorTypes.includes(error.code || '') || 
           error.metadata?.priority === 'high' ||
           error.metadata?.priority === 'critical';
  }

  private normalizeError(error: Error | unknown): ErrorDetails {
    if (error instanceof Error) {
      return {
        code: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    if (typeof error === 'string') {
      return {
        message: error,
      };
    }

    return {
      message: 'حدث خطأ غير معروف',
      metadata: { originalError: error },
    };
  }

  private logError(error: ErrorDetails) {
    const errorSeverity = this.getErrorSeverity(error);
    
    switch (errorSeverity) {
      case 'critical':
      case 'high':
        logger.error(error.message, {
          code: error.code,
          stack: error.stack,
          metadata: error.metadata,
        });
        break;
      case 'medium':
        logger.warn(error.message, {
          code: error.code,
          metadata: error.metadata,
        });
        break;
      case 'low':
        logger.info(`Error occurred: ${error.message}`, {
          code: error.code,
          metadata: error.metadata,
        });
        break;
    }
  }

  private getErrorSeverity(error: ErrorDetails): 'critical' | 'high' | 'medium' | 'low' {
    // Determine severity based on error type and context
    if (error.code === 'SecurityError' || 
        error.code === 'AuthError' ||
        error.metadata?.type === 'security_violation') {
      return 'critical';
    }
    
    if (error.code === 'NetworkError' || 
        error.code === 'DatabaseError' ||
        error.metadata?.type === 'api_unavailable') {
      return 'high';
    }
    
    if (error.code === 'ValidationError' ||
        error.metadata?.type === 'user_input_error') {
      return 'medium';
    }
    
    return 'low';
  }

  private notifyUser(error: ErrorDetails) {
    // Determine if and how to notify the user based on error type
    const shouldNotifyUser = this.shouldShowErrorToUser(error);
    
    if (shouldNotifyUser) {
      notificationService.sendNotification(
        'system', // System user ID
        this.getUserFriendlyMessage(error),
        'error',
        {
          priority: this.getErrorPriority(error),
          title: 'حدث خطأ',
          actionUrl: this.getErrorActionUrl(error),
        }
      );
    }
  }

  private shouldShowErrorToUser(error: ErrorDetails): boolean {
    // Don't show errors for network issues in development (to reduce noise)
    if (process.env.NODE_ENV === 'development' && 
        (error.code === 'NetworkError' || error.message.includes('network'))) {
      return false;
    }

    // Don't show errors for cancelled requests
    if (error.message.includes('cancelled') || error.message.includes('aborted')) {
      return false;
    }

    // Always show authentication and validation errors
    if (error.code === 'AuthError' || error.code === 'ValidationError') {
      return true;
    }

    // Show errors based on severity
    const severity = this.getErrorSeverity(error);
    return ['critical', 'high'].includes(severity);
  }

  private getUserFriendlyMessage(error: ErrorDetails): string {
    // If the error already has a user-friendly message, use it
    if (error.message && !error.message.includes('Error:') && !error.message.includes('Exception')) {
      return error.message;
    }
    
    // Map technical errors to user-friendly messages
    switch (error.code) {
      case 'AuthError':
        return 'يرجى تسجيل الدخول مرة أخرى';
      case 'ValidationError':
        return 'يرجى التحقق من صحة البيانات المدخلة';
      case 'NetworkError':
        return 'يرجى التحقق من اتصالك بالإنترنت';
      case 'ServerError':
        return 'حدث خطأ في الخادم، يرجى المحاولة لاحقاً';
      case 'NotFoundError':
        return 'لم يتم العثور على المحتوى المطلوب';
      case 'PermissionError':
        return 'ليس لديك صلاحية للوصول إلى هذا المحتوى';
      case 'RateLimitError':
        return 'تم تجاوز الحد الأقصى للطلبات، يرجى المحاولة لاحقاً';
      default:
        return 'حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى';
    }
  }

  private getErrorPriority(error: ErrorDetails): 'low' | 'medium' | 'high' | 'urgent' {
    // Determine error priority based on type and impact
    const severity = this.getErrorSeverity(error);
    
    switch (severity) {
      case 'critical':
        return 'urgent';
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
      default:
        return 'low';
    }
  }

  private getErrorActionUrl(error: ErrorDetails): string | undefined {
    // Provide action URLs for specific error types
    switch (error.code) {
      case 'AuthError':
        return '/login';
      case 'PermissionError':
        return '/dashboard/settings';
      default:
        return undefined;
    }
  }

  getErrors(): ErrorDetails[] {
    return this.errors;
  }

  clearErrors() {
    this.errors = [];
  }

  // Get error statistics for monitoring
  getErrorStats() {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    
    const recentErrors = this.errors.filter(e => 
      e.timestamp && e.timestamp > oneHourAgo
    );
    
    const errorsByType = recentErrors.reduce((acc, error) => {
      const type = error.code || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return {
      total: this.errors.length,
      recent: recentErrors.length,
      byType: errorsByType,
    };
  }
}

export const errorService = ErrorService.getInstance();